// ⚠ FROM '../constants', which is where it lives. Importing it from '../config/appConfig' --
// which exports no such name -- does NOT fail: a missing named export is `undefined`, so every
// request went to `/undefined/api/...` while lint and 38 tests stayed green. The tests stubbed
// `fetch` and never looked at the URL, which is the half that made it invisible.
import { API_BASE_URL } from '../constants';

/**
 * The planetary analog scanner's client: submit, poll, and the presentation rules.
 *
 * <h2>⚠ SUBMIT-THEN-POLL, because the first scan of a range is genuinely slow</h2>
 *
 * A cached sky grid makes a scan arithmetic — measured at 0.84s over thirty years. An UNCACHED
 * one is hundreds of thousands of ephemeris calls, measured at 2.7s for thirty years and 4.6s
 * for sixty, and a hundred-year grid is longer than the request timeout. The endpoint answers
 * 202 with a taskId and this polls it.
 *
 * <h2>The numbers this renders are COINCIDENCE, not confidence</h2>
 *
 * Bits measure how improbable an agreement is against the scanned history. They are not a
 * probability that anything happens next, and the two get conflated the moment one column is
 * labelled "confidence". The only column here about prediction is the outcome edge, and on SPY
 * it measured at t = 0.09 over sixty analogs — which is why the panel says so rather than
 * leaving a reader to assume a high bit score means a good trade.
 */

/** How long to keep polling before giving up. A 100-year cold grid is the slow case. */
export const POLL_TIMEOUT_MS = 180000;

export const POLL_INTERVAL_MS = 900;

/** The factor groups, mirrored for labelling only — the server is the source of the list. */
export const FACTOR_LABELS = {
  ASPECT: 'Geocentric aspects',
  HELIO_PHASE: 'Heliocentric phase angles',
  NAVAMSHA: 'Navamsha (D9) signs',
  DECLINATION: 'Declination parallels',
  DISTANCE: 'Distance extrema',
  STATION: 'Stations and degree returns',
  RETROGRADE: 'Retrograde state',
};

export const DEFAULTS = {
  lookbackYears: 30,
  limit: 25,
  minConfidenceScore: 0,
  minAnalogAgeDays: 365,
};

/** The horizons the server reports, in trading bars. */
export const HORIZONS = [5, 10, 20, 60];

const authHeaders = (token) => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

/** The SERVER'S own message where there is one — a 403 here means the admin gate refused. */
async function readError(response) {
  let detail = `HTTP ${response.status}`;
  try {
    const body = await response.json();
    if (body && body.error) detail = body.error;
  } catch (ignored) { /* a non-JSON error body is not worth a second failure */ }
  return new Error(detail);
}

/** What the panel should offer. Served rather than hardcoded, so a new factor group appears. */
export async function fetchOptions(token) {
  const r = await fetch(`${API_BASE_URL}/api/planetary-analog/options`,
    { headers: authHeaders(token) });
  if (!r.ok) throw await readError(r);
  return r.json();
}

/**
 * Submit a scan and poll until it finishes.
 *
 * @param onState called with 'SUBMITTING' then 'RUNNING' so the panel can say which
 */
export async function runScan(token, body, { onState, signal } = {}) {
  if (onState) onState('SUBMITTING');
  const submit = await fetch(`${API_BASE_URL}/api/planetary-analog/scan`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
    signal,
  });
  if (!submit.ok) throw await readError(submit);
  const accepted = await submit.json();
  if (!accepted || !accepted.taskId) throw new Error('The server accepted the scan without a task id.');

  if (onState) onState('RUNNING');
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    // eslint-disable-next-line no-await-in-loop
    const poll = await fetch(
      `${API_BASE_URL}/api/planetary-analog/scan/${encodeURIComponent(accepted.taskId)}`,
      { headers: authHeaders(token), signal },
    );
    if (!poll.ok) throw await readError(poll);
    // eslint-disable-next-line no-await-in-loop
    const report = await poll.json();
    if (report.status === 'FAILED') throw new Error(report.error || 'The scan failed.');
    if (report.status !== 'RUNNING') return report;
  }
  throw new Error(`The scan did not finish within ${POLL_TIMEOUT_MS / 1000}s.`);
}

/** Drop the server's cached sky grids. */
export async function clearGridCache(token) {
  const r = await fetch(`${API_BASE_URL}/api/planetary-analog/cache`,
    { method: 'DELETE', headers: authHeaders(token) });
  if (!r.ok) throw await readError(r);
  return r.json();
}

// ------------------------------------------------------------------ presentation

/** A one-line summary of what the scan actually did. */
export function describeScan(report) {
  if (!report) return '';
  const parts = [
    `${report.targetDate} back to ${report.from}`,
    `${report.days} days`,
    `${report.factorsApplicable} factors scored`,
  ];
  // ⚠ NAMED, not silently dropped. A factor excluded for never recurring is a finding --
  // "this has not happened before in the scanned history" -- and a reader comparing two runs
  // needs to see that the factor count moved for a reason.
  if (report.nonRecurringFactorCount) {
    parts.push(`${report.nonRecurringFactorCount} never recurred (excluded)`);
  }
  if (report.suppressedAsTooRecent) {
    parts.push(`${report.suppressedAsTooRecent} too recent`);
  }
  parts.push(`${report.stations} stations`);
  if (typeof report.elapsedMillis === 'number') {
    parts.push(`${(report.elapsedMillis / 1000).toFixed(1)}s${report.gridFromCache ? ' (cached)' : ''}`);
  }
  return parts.join(' · ');
}

/**
 * The mean outcome edge across the returned analogs, per horizon, with a t-statistic.
 *
 * <p>⚠ THIS IS THE ONLY THING HERE THAT IS ABOUT PREDICTION, and it is computed in the browser
 * so a reader can see it move as they change the parameters. It is the sample mean of
 * (analog's forward return − the instrument's all-day mean at the same horizon); a t near zero
 * means the analogs said nothing.
 *
 * <p>The t-statistic is OVERSTATED for the same reason every one in this repository is: the
 * windows overlap, so consecutive analogs share most of their forward period. It is a guide to
 * the sign and the rough magnitude, not a p-value.
 */
export function outcomeSummary(report) {
  const matches = (report && report.matches) || [];
  const out = {};
  HORIZONS.forEach((h) => {
    const key = `${h}d`;
    const xs = matches
      .map((m) => m.outcomeEdge && m.outcomeEdge[key])
      .filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (xs.length < 2) return;
    const mean = xs.reduce((s, v) => s + v, 0) / xs.length;
    const variance = xs.reduce((s, v) => s + (v - mean) ** 2, 0) / (xs.length - 1);
    const sd = Math.sqrt(variance);
    out[key] = {
      n: xs.length,
      mean,
      // A zero standard deviation would divide by zero; it also means every analog returned
      // exactly the same thing, which is a fixture rather than a market.
      t: sd > 0 ? mean / (sd / Math.sqrt(xs.length)) : 0,
    };
  });
  return out;
}

/**
 * The verdict a reader should take from the outcome summary.
 *
 * <p>Deliberately blunt. Four studies in this codebase have produced a confident-looking
 * positive that evaporated against its own base rate, and the thing that caught each one was
 * reading the baseline rather than the headline.
 */
export function outcomeVerdict(summary) {
  const cells = Object.values(summary || {});
  if (!cells.length) return null;
  const strong = cells.filter((c) => Math.abs(c.t) >= 2).length;
  if (!strong) {
    return {
      severity: 'info',
      text: 'No horizon separates these analogs from an ordinary day of this instrument '
        + '(|t| < 2 everywhere). The scan found structural analogs; it did not find an edge.',
    };
  }
  return {
    severity: 'warning',
    text: `${strong} of ${cells.length} horizons show |t| >= 2. Overlapping forward windows `
      + 'overstate every t here, so treat this as a reason to measure properly rather than '
      + 'as a result.',
  };
}

/** Bits, rounded for a table cell. Null stays an em dash — an absent number is not zero. */
export function num(value, places = 1) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toFixed(places);
}

export function pct(value, places = 2) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(places)}%`;
}

/** The strongest factors of one match, for the expanded row. */
export function topHits(match, howMany = 8) {
  const hits = (match && match.hits) || [];
  return hits.slice(0, howMany);
}

/** Group a match's hits by family, so the reader sees WHICH kinds agreed. */
export function hitsByGroup(match) {
  const out = {};
  ((match && match.hits) || []).forEach((h) => {
    const g = out[h.group] || { count: 0, bits: 0 };
    g.count += 1;
    g.bits += h.bits || 0;
    out[h.group] = g;
  });
  return out;
}
